<script setup>
import axios from 'axios';
import {
  BAlert,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BModal,
} from 'bootstrap-vue-next';
import { ref } from 'vue';

import { REWARD_REASONS } from '~/common/Constants';
import { parseUsernames } from '~/common/StringUtils';

import LoadingButton from './LoadingButton.vue';

const SINGLE_FAILURE_MESSAGE = 'Something went wrong and the reward was not issued. Please try again.';
const BULK_FAILURE_MESSAGE = 'Something went wrong and the rewards were not queued. Please try again.';

const blankForm = {
  usernames: [],
  note: '',
  credits: null,
  reason: '',
};
const loading = ref(false);
const errorMessage = ref(null);
const form = ref({ ...blankForm });
const props = defineProps({
  userId: {
    type: Number,
    required: false,
    default: null,
  },
});
const model = defineModel({ type: Boolean });
const emit = defineEmits(['rewardIssued']);

const reasonOptions = Object.entries(REWARD_REASONS).map(([value, text]) => ({
  value,
  text,
}));

const resetForm = () => {
  form.value = { ...blankForm };
  errorMessage.value = null;
};

const issueReward = async (event) => {
  event.preventDefault();
  if (loading.value) {
    return;
  }

  loading.value = true;
  errorMessage.value = null;

  const formData = { ...form.value };

  let endpoint;
  let failureMessage;
  if (props.userId != null) {
    endpoint = '/api/v1/rewards/issue';
    failureMessage = SINGLE_FAILURE_MESSAGE;
    formData.user_id = props.userId;
  } else {
    endpoint = '/api/v1/rewards/bulk_issue';
    failureMessage = BULK_FAILURE_MESSAGE;
    formData.usernames = parseUsernames(formData.usernames);
  }

  try {
    await axios.post(endpoint, formData);
  } catch (error) {
    errorMessage.value = error.response?.data?.error ?? failureMessage;

    return;
  } finally {
    loading.value = false;
  }

  emit('rewardIssued', form.value);
  model.value = false;
};
</script>

<template>
  <BModal
    v-model="model"
    title="Issue reward"
    no-footer
    @hidden="resetForm"
  >
    <BAlert
      :model-value="errorMessage != null"
      variant="danger"
      class="mb-3"
    >
      {{ errorMessage }}
    </BAlert>
    <BForm @submit="issueReward">
      <BFormGroup
        v-if="userId == null"
        label="Users (one per line)"
        class="mb-3"
      >
        <BFormTextarea
          v-model="form.usernames"
          required
          aria-required="true"
          data-test-id="reward-modal-textarea-usernames"
        />
      </BFormGroup>
      <BFormGroup
        label="Credits"
        class="mb-3"
      >
        <BFormInput
          v-model="form.credits"
          type="number"
          required
          aria-required="true"
          data-test-id="reward-modal-input-credits"
        />
      </BFormGroup>
      <BFormGroup
        label="Reason"
        description="Not added to the contributor issue"
        class="mb-3"
      >
        <BFormSelect
          v-model="form.reason"
          :options="reasonOptions"
          required
          aria-required="true"
          data-test-id="reward-modal-select-reason"
        />
      </BFormGroup>
      <BFormGroup
        label="Note (optional)"
        description="Added to the contributor issue, use markdown"
        class="mb-3"
      >
        <BFormTextarea
          v-model="form.note"
          aria-required="false"
          data-test-id="reward-modal-textarea-note"
        />
      </BFormGroup>
      <LoadingButton
        type="submit"
        class="float-end"
        variant="primary"
        :loading="loading"
        loading-text="Issuing..."
      >
        Issue reward
      </LoadingButton>
    </BForm>
  </BModal>
</template>
